import { getEntriesWithFilter } from '@/lib/contentstack-sdk';
import { IHousePlanHit } from '@/lib/search/types';
import { getCachedInitialProps } from '@/components/helpers/fetchInitialProps';

/**
 * Fetch all model homes from internal Algolia API
 */
async function fetchModelHomesBase(): Promise<IHousePlanHit[]> {
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_API_BASE_URL}/algolia/model-homes`,
  );

  if (!res.ok) {
    throw new Error(`Failed to fetch model homes: ${res.statusText}`);
  }

  return await res.json();
}

/**
 * Enrich model homes with CMS data
 */
async function fetchAllModelHomeCMS(): Promise<any[]> {
  try {
    const enriched = (await getEntriesWithFilter({
      contentTypeUid: 'tm_model_home_detail',
      filters: {},
    })) as any[];

    return enriched || [];
  } catch (err) {
    console.error('Error fetching CMS model home entries:', err);
    return [];
  }
}

/**
 * Create a key like `studioId|elevationId` for fast lookup
 */
function createKey(studioId: string | number, elevationId: string | number) {
  return `${studioId}|${elevationId}`;
}

/**
 * Map enrichments to Algolia model homes
 */
async function mergeModelHomeData(
  baseData: IHousePlanHit[],
  cmsData: any[],
): Promise<IHousePlanHit[]> {
  const cmsMap = new Map<string, any>();

  for (const entry of cmsData) {
    const studioId = entry?.studio_id?.location;
    const elevationId = entry?.model_home_id?.model_id;

    if (studioId && elevationId) {
      cmsMap.set(createKey(studioId, elevationId), entry);
    }
  }

  const { studioData: allStudios } = await getCachedInitialProps();
  baseData = baseData.slice().sort((a, b) => {
    // Sort by ExteriorStyle.Name, then by city, then by state
    const nameA = a.ExteriorStyle?.Name || '';
    const nameB = b.ExteriorStyle?.Name || '';
    const nameCompare = nameA.localeCompare(nameB);
    if (nameCompare !== 0) return nameCompare;

    const studiosArray = allStudios;
    const studioA = studiosArray?.find(
      (x) => x.Id == a.FloorPlans?.[0]?.StudioId,
    );
    const studioB = studiosArray?.find(
      (x) => x.Id == b.FloorPlans?.[0]?.StudioId,
    );
    if (studioA && studioB) {
      const cityA = studioA?.City || '';
      const cityB = studioB?.City || '';
      const cityCompare = cityA.localeCompare(cityB);
      if (cityCompare !== 0) return cityCompare;

      const stateA = studioA?.State || '';
      const stateB = studioB?.State || '';
      return stateA.localeCompare(stateB);
    }

    return 0;
  });

  return baseData.map((home) => {
    const floorPlan = home?.FloorPlans?.[0];
    if (!floorPlan) return home;

    const cms =
      cmsMap.get(createKey(floorPlan.StudioId, floorPlan.ElevationId)) ?? null;
    return { ...home, cms };
  });
}

/**
 * Get all model homes merged with CMS data
 */
export async function getModelHomes(): Promise<IHousePlanHit[]> {
  const [base, cms] = await Promise.all([
    fetchModelHomesBase(),
    fetchAllModelHomeCMS(),
  ]);
  return await mergeModelHomeData(base, cms);
}

/**
 * Get only model homes for a specific studio
 */
export async function getStudioModelHomes(
  studioId: number,
): Promise<IHousePlanHit[]> {
  const all = await getModelHomes();
  return all.filter((home) =>
    home?.FloorPlans?.some((fp) => fp.StudioId === studioId),
  );
}

/**
 * Filter a local model home array by studio
 */
export function filterStudioModelHomes(
  modelHomes: IHousePlanHit[],
  studioId: number,
): IHousePlanHit[] {
  return modelHomes.filter((modelHome) =>
    modelHome?.FloorPlans?.some((fp) => fp.StudioId === studioId),
  );
}
